/**
 * Tutorials Page
 * 
 * Video tutorial showcase page
 * 
 * Version: 6.2.0
 */
import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TUTORIALS, CATEGORIES, Tutorial, TutorialCategory } from '../data/tutorials'
import VideoTutorialCard, { FeaturedTutorialCard, CompactTutorialCard } from '../components/VideoTutorialCard'
import VideoPlayerModal from '../components/VideoPlayerModal'
import PageHeader from '../components/PageHeader'
import './Tutorials.css'

const Tutorials: React.FC = () => {
  const { t } = useTranslation()
  const [activeCategory, setActiveCategory] = useState<TutorialCategory | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTutorial, setSelectedTutorial] = useState<Tutorial | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Filter tutorials
  const filteredTutorials = useMemo(() => {
    return TUTORIALS.filter(tutorial => {
      // Category filter
      if (activeCategory !== 'all' && tutorial.category !== activeCategory) {
        return false
      }
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        return (
          tutorial.title.toLowerCase().includes(query) ||
          tutorial.description.toLowerCase().includes(query) ||
          tutorial.tags.some(tag => tag.toLowerCase().includes(query))
        )
      }
      return true
    })
  }, [activeCategory, searchQuery])

  // Get featured tutorial
  const featuredTutorial = useMemo(() => {
    return TUTORIALS.find(t => t.featured && t.category === 'getting-started')
  }, [])

  // Get tutorial count by category
  const getCategoryCount = (category: TutorialCategory) => {
    return TUTORIALS.filter(t => t.category === category).length
  }

  // Handle play
  const handlePlay = (tutorial: Tutorial) => {
    setSelectedTutorial(tutorial)
    setIsModalOpen(true)
  }

  // Handle close
  const handleClose = () => {
    setIsModalOpen(false)
    setSelectedTutorial(null)
  }

  return (
    <div className="tutorials-page">
      <PageHeader
        title="Video Tutorials"
        subtitle="Learn how to use ZO Bull with our comprehensive video guides"
        icon="📚"
      />

      {/* Search & Filter */}
      <div className="tutorials-controls">
        <div className="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search tutorials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="category-filters">
          <button
            className={`filter-btn ${activeCategory === 'all' ? 'active' : ''}`}
            onClick={() => setActiveCategory('all')}
          >
            All ({TUTORIALS.length})
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              className={`filter-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.icon} {cat.name} ({getCategoryCount(cat.id)})
            </button>
          ))}
        </div>
      </div>

      {/* Featured Tutorial */}
      {featuredTutorial && activeCategory === 'all' && !searchQuery && (
        <section className="featured-section">
          <FeaturedTutorialCard 
            tutorial={featuredTutorial}
            onPlay={handlePlay}
          />
        </section>
      )}

      {/* All Tutorials Grid */}
      <section className="tutorials-grid-section">
        <div className="section-header">
          <h2>
            {activeCategory === 'all' 
              ? 'All Tutorials' 
              : CATEGORIES.find(c => c.id === activeCategory)?.name
            }
          </h2>
          <span className="tutorial-count">{filteredTutorials.length} tutorials</span>
        </div>

        {filteredTutorials.length > 0 ? (
          <div className="tutorials-grid">
            {filteredTutorials.map(tutorial => (
              <VideoTutorialCard
                key={tutorial.id}
                tutorial={tutorial}
                onPlay={handlePlay}
              />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <span className="empty-icon">🔍</span>
            <h3>No tutorials found</h3>
            <p>Try adjusting your search or filter criteria</p>
            <button onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}>
              Clear filters
            </button>
          </div>
        )}
      </section>

      {/* Quick Access Sidebar */}
      <aside className="quick-access-sidebar">
        <h3>Quick Access</h3>
        <div className="quick-tutorials">
          {TUTORIALS.slice(0, 5).map(tutorial => (
            <CompactTutorialCard
              key={tutorial.id}
              tutorial={tutorial}
              onPlay={handlePlay}
            />
          ))}
        </div>
      </aside>

      {/* Video Player Modal */}
      <VideoPlayerModal
        tutorial={selectedTutorial}
        isOpen={isModalOpen}
        onClose={handleClose}
        onComplete={(tutorial) => {
          console.log('Tutorial completed:', tutorial.id)
        }}
      />
    </div>
  )
}

export default Tutorials